import React, { useEffect, useState, useRef } from "react";
import axiosInstance from "../utils/AxiosInstance";
import {
  Save,
  Download,
  FileJson,
  FileText,
  Edit,
  Upload
} from "lucide-react";

const MITRE_TACTICS = [
  "reconnaissance",
  "resource-development",
  "initial-access",
  "execution",
  "persistence",
  "privilege-escalation",
  "defense-evasion",
  "credential-access",
  "discovery",
  "lateral-movement",
  "collection",
  "command-and-control",
  "exfiltration",
  "impact",
];

function formatTacticName(tactic) {
  return tactic
    .split("-")
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(" ");
}

function interpolateColor(color1, color2, factor) {
  const result = color1.slice();
  for (let i = 0; i < 3; i++) {
    result[i] = Math.round(result[i] + factor * (color2[i] - color1[i]));
  }
  return result;
}

function rgbToCss(rgb) {
  return `rgb(${rgb[0]},${rgb[1]},${rgb[2]})`;
}

function rgbToHex(rgb) {
  return (
    "#" +
    rgb
      .map((c) => {
        const hex = c.toString(16);
        return hex.length === 1 ? "0" + hex : hex;
      })
      .join("")
  );
}

const DEFAULT_COLOR_LOW = [34, 197, 94];
const DEFAULT_COLOR_HIGH = [220, 38, 38];



export default function ThreatActorHeatmap() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [zoom, setZoom] = useState(1);
  const [colorLow, setColorLow] = useState(DEFAULT_COLOR_LOW);
  const [colorHigh, setColorHigh] = useState(DEFAULT_COLOR_HIGH);
  const [viewMode, setViewMode] = useState("all");
  const [showCounts, setShowCounts] = useState(true);
  const heatmapRef = useRef();
  const [showTopTechniquesOnly, setShowTopTechniquesOnly] = useState(false);
  const [topTechniqueLimit, setTopTechniqueLimit] = useState(10);
  const [aiSummary, setAiSummary] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  const trackedActors = [];
  const groupByParent = true;
  const topTechniquesOnly = true;
  const topCount = 10;
  const [audience, setAudience] = useState("all");

  // Save/load heatmaps
  const [savedHeatmaps, setSavedHeatmaps] = useState([]);

  const [currentHeatmapName, setCurrentHeatmapName] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [selectedActor, setSelectedActor] = useState("");
  
  const fetchAISummary = async (audience = "all") => {
    setAiLoading(true);
    setAiError(null);
    setAiSummary(null);

    try {
      const res = await axiosInstance.post("/insights/actors/tracked/ai-summary/", {
        tracked_actors: trackedActors,
        group_by_parent: groupByParent,
        top_techniques: topTechniquesOnly,
        top_count: topCount,
        audience,  // Include audience in request
      });
      setAiSummary(res.data.summary);
    } catch (err) {
      console.error("Failed to fetch AI summary", err);
      setAiError(err.response?.data?.error || "Failed to generate summary");
    } finally {
      setAiLoading(false);
    }
  };

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      setError("");
      try {
        const res = await axiosInstance.get("/insights/actors/tracked/heatmap/");
        setData(res.data);
      } catch (err) {
        console.error(err);
        setError("Failed to load heatmap data.");
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  useEffect(() => {
    async function fetchSavedHeatmaps() {
        try {
        const res = await axiosInstance.get("/insights/actors/heatmaps/");
        setSavedHeatmaps(res.data);  // assume it returns a list of { name, id }
        } catch (err) {
        console.error("Failed to fetch saved heatmaps", err);
        }
    }
    fetchSavedHeatmaps();
    }, []);

  const tacticMap = {};
  let maxCount = 1;
  MITRE_TACTICS.forEach((t) => (tacticMap[t] = []));
maxCount = 1;

data.forEach(({ tactic, name, count, parent_technique, technique_id, tracked_actors }) => {
  // STEP 1: Filter by selected actor if set
  if (selectedActor && (!tracked_actors || !tracked_actors.includes(selectedActor))) {
    return;
  }

  // Ensure tactic bucket exists
  if (!tacticMap[tactic]) tacticMap[tactic] = [];

  // STEP 2: Handle grouping
  if (viewMode === "grouped" && parent_technique) {
    const parentName = parent_technique.name;
    const existing = tacticMap[tactic].find((entry) => entry.name === parentName);

    if (existing) {
      existing.count += count;
      existing.children.push({ name, count });
    } else {
      tacticMap[tactic].push({
        name: parentName,
        count,
        children: [{ name, count }],
      });
    }
  } else {
    tacticMap[tactic].push({ name, count, children: [], technique_id });
  }

  // STEP 3: Track max count for color scaling
  if (viewMode === "grouped" && parent_technique) {
    const parent = tacticMap[tactic].find((e) => e.name === parent_technique.name);
    if (parent && parent.count > maxCount) maxCount = parent.count;
  } else {
    if (count > maxCount) maxCount = count;
  }
});

// STEP 4: Filter out empty tactics
const displayedTactics = MITRE_TACTICS.filter(
  (t) => tacticMap[t] && tacticMap[t].length > 0
);



  let topTechniquesSet = new Set();

    if (showTopTechniquesOnly) {
      const allEntries = [];

      MITRE_TACTICS.forEach((tactic) => {
        (tacticMap[tactic] || []).forEach(({ name, count, children }) => {
          // Always push the parent technique
          allEntries.push({ tactic, name, count });

          // Only consider children in 'all' view mode
          if (viewMode !== "grouped" && Array.isArray(children)) {
            children.forEach((child) => {
              allEntries.push({ tactic, name: child.name, count: child.count });
            });
          }
        });
      });

      // Sort all entries descending by count
      allEntries.sort((a, b) => b.count - a.count);

      // Determine cutoff count
      const cutoffCount = allEntries[topTechniqueLimit - 1]?.count ?? 0;

      // Include all entries with count >= cutoff
      const topEntries = allEntries.filter((e) => e.count >= cutoffCount);

      // Build the final set
      topTechniquesSet = new Set(topEntries.map(({ tactic, name }) => `${tactic}|||${name}`));
    }



  function getHeatColor(count) {
    if (count === 0) return "#1f2937";
    const factor = count / maxCount;
    const rgb = interpolateColor(colorLow, colorHigh, factor);
    return rgbToCss(rgb);
  }

  function exportCSV() {
    let csv = "Tactic,Technique,Count\n";
    data.forEach(({ tactic, name, count }) => {
      csv += `"${formatTacticName(tactic)}","${name}",${count}\n`;
    });
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "threat_actor_heatmap.csv";
    a.click();
    URL.revokeObjectURL(url);
  }

  function exportJSON() {
    const json = JSON.stringify(data, null, 2);
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "threat_actor_heatmap.json";
    a.click();
    URL.revokeObjectURL(url);
  }
  function handleToggleEditMode() {
    setIsEditing((prev) => !prev);
    }
  
  function exportSVG() {
    const svgElem = generateSVG();
    const serializer = new XMLSerializer();
    const source = serializer.serializeToString(svgElem);

    const blob = new Blob([source], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "threat_actor_heatmap.svg";
    link.click();
    URL.revokeObjectURL(url);
  }
  function generateSVG() {
  const svgNS = "http://www.w3.org/2000/svg";
  const svgElem = document.createElementNS(svgNS, "svg");
  svgElem.setAttribute("xmlns", svgNS);
  svgElem.setAttribute("width", displayedTactics.length * 180);
  svgElem.setAttribute("height", "1000");

  displayedTactics.forEach((tactic, colIdx) => {
    const x = colIdx * 180;

    const group = document.createElementNS(svgNS, "g");
    group.setAttribute("transform", `translate(${x}, 0)`);

    const title = document.createElementNS(svgNS, "text");
    title.setAttribute("x", "10");
    title.setAttribute("y", "20");
    title.setAttribute("fill", "white");
    title.setAttribute("font-size", "14");
    title.textContent = formatTacticName(tactic);
    group.appendChild(title);

    tacticMap[tactic].forEach(({ name, count }, rowIdx) => {
      if (showTopTechniquesOnly && !topTechniquesSet.has(`${tactic}|||${name}`)) return;
      const y = 40 + rowIdx * 60;

      const rect = document.createElementNS(svgNS, "rect");
      rect.setAttribute("x", "10");
      rect.setAttribute("y", y.toString());
      rect.setAttribute("width", "160");
      rect.setAttribute("height", "40");
      rect.setAttribute("fill", getHeatColor(count));
      rect.setAttribute("rx", "6");
      group.appendChild(rect);

      const label = document.createElementNS(svgNS, "text");
      label.setAttribute("x", "15");
      label.setAttribute("y", (y + 25).toString());
      label.setAttribute("font-size", "12");
      label.setAttribute("fill", "white");
      label.textContent = name.length > 20 ? name.slice(0, 18) + "…" : name;
      group.appendChild(label);

      if (showCounts) {
        const countLabel = document.createElementNS(svgNS, "text");
        countLabel.setAttribute("x", "165");
        countLabel.setAttribute("y", (y + 25).toString());
        countLabel.setAttribute("font-size", "12");
        countLabel.setAttribute("fill", "white");
        countLabel.setAttribute("text-anchor", "end");
        countLabel.textContent = count;
        group.appendChild(countLabel);
      }
    });

    svgElem.appendChild(group);
  });

  return svgElem;
}

  function Toggle({ label, checked, onChange }) {
    return (
      <div className="flex items-center justify-between">
        <span className="text-sm text-gray-300">{label}</span>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            className="sr-only peer"
            checked={checked}
            onChange={onChange}
          />
          <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-purple-500 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
        </label>
      </div>
    );
  }
  // Save heatmap to localStorage
  async function saveHeatmap(name) {
    try {
        await axiosInstance.post("/insights/actors/heatmaps/save/", {
        name,
        data,
        });
        setSaveName("");
        // Re-fetch list
        const res = await axiosInstance.get("/insights/actors/heatmaps/");
        setSavedHeatmaps(res.data);
    } catch (err) {
        console.error("Failed to save heatmap", err);
        alert("Failed to save heatmap.");
    }
    }

  // Load heatmap from savedHeatmaps
  async function loadHeatmap(name) {
  try {
    const heatmap = savedHeatmaps.find(h => h.name === name);
    if (!heatmap) {
      alert("Heatmap not found.");
      return;
    }
    const res = await axiosInstance.get(`/insights/actors/heatmaps/${heatmap.id}/`);
    setData(res.data.data);  // assuming backend returns { name, data }
    setCurrentHeatmapName(name);
  } catch (err) {
    console.error("Failed to load heatmap", err);
    alert("Could not load heatmap.");
  }
}
  // Reset to default tracked actors heatmap (re-fetch)
  function resetToTrackedActors() {
    setCurrentHeatmapName("");
    (async () => {
      try {
        const res = await axiosInstance.get("/insights/actors/tracked/heatmap/");
        setData(res.data);
      } catch (err) {
        console.error(err);
        setError("Failed to load heatmap data.");
      }
    })();
  }

  // Handler for heatmap selection dropdown
  function handleLoadSavedHeatmap(name) {
    if (!name) return;
    if (name === "__tracked") {
      resetToTrackedActors();
    } else {
      loadHeatmap(name);
    }
  }

  // Handler for saving heatmap
  function handleSaveHeatmap() {
    if (!saveName) {
      alert("Please enter a name to save the heatmap.");
      return;
    }
    saveHeatmap(saveName);
    setSaveName("");
  }

  // Handle editing the count of a technique
  function handleEditCount(tactic, technique_id, newCount) {
    if (isNaN(newCount) || newCount < 0) return;
    setData((prevData) =>
      prevData.map((entry) =>
        entry.tactic === tactic && entry.technique_id === technique_id
          ? { ...entry, count: newCount }
          : entry
      )
    );
  }

  // Handle removing a technique from the heatmap
  function handleRemoveEntry(tactic, technique_id) {
    setData((prevData) =>
      prevData.filter(
        (entry) => !(entry.tactic === tactic && entry.technique_id === technique_id)
      )
    );
  }

    // handle importing a MITRE layer
  function handleImportMitreLayer(layerData) {
  if (!layerData?.techniques || !Array.isArray(layerData.techniques)) {
    alert("Invalid MITRE layer format.");
    return;
  }

  setData((prevData) => {
    const updatedData = [...prevData];

    for (const { techniqueID, tactic, displayName, techniqueName } of layerData.techniques) {
      if (!techniqueID || !tactic) continue;

      const readableName = displayName || techniqueName || techniqueID;

      const existingIdx = updatedData.findIndex(
        (entry) => entry.tactic === tactic && entry.technique_id === techniqueID
      );

      if (existingIdx !== -1) {
        updatedData[existingIdx].count += 1;
      } else {
        updatedData.push({
          tactic,
          name: readableName,             // for display
          technique_id: techniqueID,      // used for matching
          count: 1,
        });
      }
    }

    return updatedData;
  });

  alert("Layer imported and heatmap updated.");
}

return (
  <div className="p-6 bg-gray-900 rounded shadow-md">
    <h2 className="text-2xl font-bold text-white mb-6">
      Threat Actor Techniques Heatmap
    </h2>

    <div className="mb-6 p-6 bg-gray-800 rounded-xl shadow-md ring-1 ring-gray-700 flex flex-col justify-between h-full">
      <h3 className="text-xl font-semibold text-white mb-4">
        Heatmap Controls
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 flex-1">
        {/* Zoom & Toggles */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300">Zoom</label>
            <div className="flex items-center gap-3">
              <input
                type="range"
                min="0.5"
                max="2"
                step="0.05"
                value={zoom}
                onChange={(e) => setZoom(parseFloat(e.target.value))}
                className="w-full"
              />
              <span className="text-gray-400">{(zoom * 100).toFixed(0)}%</span>
            </div>
          </div>

          <div className="flex flex-col gap-3">
            <Toggle
              label="Show Counts"
              checked={showCounts}
              onChange={() => setShowCounts((prev) => !prev)}
            />
            <Toggle
              label="Group by Parent"
              checked={viewMode === "grouped"}
              onChange={() => setViewMode((prev) => (prev === "grouped" ? "all" : "grouped"))}
            />
            
            <div>
              <Toggle
                label="Top Techniques"
                checked={showTopTechniquesOnly}
                onChange={() => setShowTopTechniquesOnly((prev) => !prev)}
              />
              {showTopTechniquesOnly && (
                <div className="mt-2 ml-6">
                  <input
                    type="number"
                    min="1"
                    value={topTechniqueLimit}
                    onChange={(e) => setTopTechniqueLimit(parseInt(e.target.value))}
                    className="w-20 bg-gray-700 text-white px-2 py-1 rounded text-sm border border-gray-600"
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Actor Filter & Color Selectors */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300">Filter by Actor</label>
            <select
              value={selectedActor}
              onChange={(e) => setSelectedActor(e.target.value)}
              className="w-full bg-gray-700 text-white border border-gray-600 rounded px-3 py-2 mt-1"
            >
              <option value="">All Actors</option>
              {[...new Set(data.flatMap((d) => d.tracked_actors || []))].sort().map((actor) => (
                <option key={actor} value={actor}>{actor}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-4">
            <label className="text-sm text-gray-300">Low</label>
            <input
              type="color"
              value={rgbToHex(colorLow)}
              onChange={(e) => {
                const r = parseInt(e.target.value.substr(1, 2), 16);
                const g = parseInt(e.target.value.substr(3, 2), 16);
                const b = parseInt(e.target.value.substr(5, 2), 16);
                setColorLow([r, g, b]);
              }}
              className="w-10 h-6 p-0 border-0 rounded"
            />
            <label className="text-sm text-gray-300">High</label>
            <input
              type="color"
              value={rgbToHex(colorHigh)}
              onChange={(e) => {
                const r = parseInt(e.target.value.substr(1, 2), 16);
                const g = parseInt(e.target.value.substr(3, 2), 16);
                const b = parseInt(e.target.value.substr(5, 2), 16);
                setColorHigh([r, g, b]);
              }}
              className="w-10 h-6 p-0 border-0 rounded"
            />
          </div>
        </div>

        {/* Save/Load Heatmap */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300">Saved Heatmaps</label>
            <select
              onChange={(e) => handleLoadSavedHeatmap(e.target.value)}
              className="w-full bg-gray-700 text-white border border-gray-600 rounded px-3 py-2 mt-1"
              value={currentHeatmapName || ""}
            >
              <option value="">Select...</option>
              {savedHeatmaps.map(({ name }) => (
                <option key={name} value={name}>{name}</option>
              ))}
              <option value="__tracked">Load Tracked Actors</option>
            </select>
          </div>

          <div className="flex gap-2">
            <input
              type="text"
              placeholder="Save name"
              value={saveName}
              onChange={(e) => setSaveName(e.target.value)}
              className="flex-1 bg-gray-700 text-white border border-gray-600 rounded px-2 py-1"
            />
            <button
              onClick={handleSaveHeatmap}
              className="bg-yellow-600 hover:bg-yellow-700 text-white px-3 py-1 rounded"
            >
              Save
            </button>
          </div>
        </div>
      </div>

      {/* Bottom-aligned button row */}
      <div className="mt-8 pt-4 border-t border-gray-700 flex flex-wrap gap-3 justify-center sm:justify-end">
        <button
          onClick={handleSaveHeatmap}
          className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg text-sm font-medium shadow"
        >
          <Save className="w-4 h-4" />
          Save
        </button>

        <button
          onClick={exportCSV}
          className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium shadow"
        >
          <FileText className="w-4 h-4" />
          CSV
        </button>

        <button
          onClick={exportJSON}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium shadow"
        >
          <FileJson className="w-4 h-4" />
          JSON
        </button>

        <button
          onClick={exportSVG}
          className="inline-flex items-center gap-2 px-4 py-2 bg-purple-700 hover:bg-purple-800 text-white rounded-lg text-sm font-medium shadow"
        >
          <Download className="w-4 h-4" />
          SVG
        </button>

        <button
          onClick={handleToggleEditMode}
          className="inline-flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-sm font-medium shadow"
        >
          <Edit className="w-4 h-4" />
          {isEditing ? "Done" : "Edit"}
        </button>

        <input
          type="file"
          accept=".json"
          onChange={(e) => {
            if (e.target.files.length > 0) {
              const reader = new FileReader();
              reader.onload = () => {
                try {
                  const json = JSON.parse(reader.result);
                  handleImportMitreLayer(json);
                } catch (err) {
                  console.error("Invalid JSON:", err);
                  alert("Failed to parse JSON.");
                }
              };
              reader.readAsText(e.target.files[0]);
            }
          }}
          className="hidden"
          id="mitre-layer-upload"
        />
        <label
          htmlFor="mitre-layer-upload"
          className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium shadow cursor-pointer"
        >
          <Upload className="w-4 h-4" />
          Import
        </label>
      </div>
    </div>
<div className="mt-8 mb-8 p-6 bg-white dark:bg-gray-900 rounded-xl border border-gray-300 dark:border-gray-700">
  <label htmlFor="audience-select" className="block mb-2 font-semibold text-gray-700 dark:text-gray-300">
    Select Audience for AI Summary:
  </label>
  <select
    id="audience-select"
    value={audience}
    onChange={(e) => setAudience(e.target.value)}
    className="w-full mb-4 px-3 py-2 border border-gray-400 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-purple-600"
  >
    <option value="all">Full Report</option>
    <option value="leadership">Leadership & Board</option>
    <option value="technical">Technical Teams</option>
    <option value="risk">Risk, Legal & Compliance</option>
    <option value="fraud">Fraud & Privacy</option>
    <option value="training">Training & Awareness</option>
  </select>

  <button
    onClick={() => fetchAISummary(audience)}
    disabled={aiLoading}
    className={`w-full px-4 py-2 font-semibold rounded-md ${
      aiLoading
        ? "bg-gray-400 cursor-not-allowed"
        : "bg-purple-600 hover:bg-purple-700 text-white"
    }`}
  >
    {aiLoading ? "Generating Summary..." : "Generate AI Summary of TTPs"}
  </button>

  {aiError && (
    <p className="mt-3 text-red-500 dark:text-red-400">{aiError}</p>
  )}

  {aiSummary && (
    <div className="mt-6 whitespace-pre-line text-gray-800 dark:text-gray-200">
      <h3 className="font-semibold mb-2 text-purple-700 dark:text-purple-300">
        AI Summary:
      </h3>
      <p>{aiSummary}</p>
    </div>
  )}
</div>

    {loading && <p className="text-gray-400">Loading heatmap data...</p>}
    {error && <p className="text-red-500">{error}</p>}

    {!loading && !error && (
      <div
        className="border border-gray-700 overflow-auto max-w-full max-h-[600px] bg-gray-800 rounded"
        style={{ padding: 8 }}
      >
        <div className="mb-4 mt-2">
          <div className="mt-6 text-sm text-white">
            <div className="mb-1 flex justify-between px-1">
              <span>Low Activity</span>
              <span>High Activity</span>
            </div>
            <div
              className="h-4 w-full rounded-md border border-gray-700"
              style={{
                background: `linear-gradient(to right, rgb(${colorLow.join(',')}), rgb(${colorHigh.join(',')}))`,
              }}
            />
          </div>
        </div>
        <div
          ref={heatmapRef}
          className="flex space-x-2"
          style={{ transform: `scale(${zoom})`, transformOrigin: "top left" }}
        >
          {displayedTactics.map((tactic) => (
            <div key={tactic} className="flex flex-col">
              <div
                className="sticky top-0 bg-gray-900 text-white font-semibold text-center px-3 py-2 border border-gray-700 rounded-t-md"
                style={{ minWidth: 160, zIndex: 10 }}
                title={formatTacticName(tactic)}
              >
                {formatTacticName(tactic)}
              </div>

              {tacticMap[tactic].map(({ name, count, children = [], technique_id }, idx) => {
                if (showTopTechniquesOnly && !topTechniquesSet.has(`${tactic}|||${name}`)) {
                  return null;
                }
                const bgColor = getHeatColor(count);
                return (
                  <div key={idx} className="border border-gray-700 text-sm p-2 bg-opacity-80 rounded-md shadow-sm" style={{ backgroundColor: bgColor, minWidth: 160, minHeight: 40 }}>
                    <div className="flex items-center justify-between text-white font-semibold truncate">
                      <span className="truncate">{name}</span>
                      {showCounts && (
                        isEditing ? (
                          <input
                            type="number"
                            min="0"
                            value={count}
                            onChange={(e) =>
                              handleEditCount(tactic, technique_id, parseInt(e.target.value))
                            }
                            className="w-16 bg-gray-700 text-white px-2 py-0.5 rounded text-xs font-semibold ml-2"
                          />
                        ) : (
                          <span className="ml-2 text-sm font-bold text-white bg-gray-700/30 px-1.5 py-0.5 rounded">
                            {count}
                          </span>
                        )
                      )}
                    </div>

                    {isEditing && showCounts && (
                      <div className="text-right mt-1">
                        <button
                          onClick={() => handleRemoveEntry(tactic, technique_id)}
                          className="text-red-400 hover:text-red-600 text-xs"
                          title="Remove technique"
                        >
                          ✕ Remove
                        </button>
                      </div>
                    )}

                    {viewMode === "grouped" && children.length > 0 && (
                      <div className="mt-2 ml-2 border-t border-gray-600 pt-1 space-y-1">
                        {children.map((child, i) => (
                          <div key={i} className="text-xs text-gray-300 flex justify-between bg-gray-700/30 px-1 rounded">
                            <span className="truncate">{child.name}</span>
                            <span>{child.count}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    )}
  </div>
);
}
